/**
 * Browser sharing helpers. A cancelled native share is a distinct outcome;
 * rejected clipboard writes and blocked popups must not look successful.
 */
export interface ShareOptions {
  title: string;
  text: string;
  url?: string;
}

export async function copyToClipboard(text: string): Promise<void> {
  if (!navigator.clipboard?.writeText) {
    throw new Error('Clipboard access is unavailable.');
  }
  await navigator.clipboard.writeText(text);
}

export async function shareNative(
  options: ShareOptions
): Promise<'shared' | 'cancelled' | 'unavailable'> {
  if (!navigator.share || !window.isSecureContext) return 'unavailable';

  try {
    await navigator.share({
      title: options.title,
      text: options.text,
      url: options.url || window.location.href
    });
    return 'shared';
  } catch (error) {
    if (typeof error === 'object' && error !== null && 'name' in error && error.name === 'AbortError') {
      return 'cancelled';
    }
    throw error;
  }
}

export function shareToPlatform(platform: string, options: ShareOptions): boolean {
  const encodedUrl = encodeURIComponent(options.url || window.location.href);
  const encodedText = encodeURIComponent(options.text);
  const encodedTitle = encodeURIComponent(options.title);

  const urls: Record<string, string> = {
    twitter: `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
    reddit: `https://www.reddit.com/submit?url=${encodedUrl}&title=${encodedTitle}`
  };

  if (!urls[platform]) return false;

  const width = 550;
  const height = 400;
  const left = Math.max(0, (window.screen.width - width) / 2);
  const top = Math.max(0, (window.screen.height - height) / 2);
  const popup = window.open(
    urls[platform],
    `Share on ${platform}`,
    `width=${width},height=${height},left=${left},top=${top},popup=yes`
  );
  if (!popup) return false;
  popup.opener = null;
  return true;
}

export const SHARE_PLATFORMS = [
  { key: 'twitter', name: 'Twitter' },
  { key: 'facebook', name: 'Facebook' },
  { key: 'linkedin', name: 'LinkedIn' },
  { key: 'reddit', name: 'Reddit' }
] as const;
